import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  CircleUserRound,
  LogOut,
  Settings,
  SlidersHorizontal,
  LayoutDashboard,
  UserRound,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";

export default function MemberAccountMenu() {
  const { user, checkingAuth, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState("");

  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setError("");
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleLogout = async () => {
    setLoggingOut(true);
    setError("");

    try {
      await logout();
      setOpen(false);

      if (
        location.pathname === "/stream-presets" ||
        location.pathname === "/members"
      ) {
        navigate("/members", { replace: true });
      }
    } catch {
      setError("Unable to log out. Please try again.");
    } finally {
      setLoggingOut(false);
    }
  };

  // This is the member menu, not the administrator dashboard.
  // Keep it off the separate Comik Releaf storefront domains.
  const hostname = window.location.hostname;

  if (
    hostname === "comikreleaf.com" ||
    hostname === "comikreleaf.ca"
  ) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className="fixed right-4 top-3 z-[90] sm:right-6"
    >
      {checkingAuth ? (
        <div
          className="rounded-lg border border-matrix-green/20
                     bg-black/80 px-4 py-3 text-xs
                     text-matrix-green/60"
        >
          Checking account...
        </div>
      ) : !user ? (
        <Link
          to="/members"
          className="flex items-center gap-2 rounded-lg
                     border border-matrix-green/40
                     bg-black/90 px-4 py-3
                     text-xs font-semibold uppercase tracking-wider
                     text-matrix-green shadow-lg
                     hover:bg-matrix-green/10"
        >
          <CircleUserRound size={18} />
          Sign In / Join Free
        </Link>
      ) : (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            aria-haspopup="menu"
            className="flex items-center gap-2 rounded-lg
                       border border-matrix-green/40
                       bg-black/90 px-3 py-2.5
                       text-matrix-green shadow-lg
                       hover:bg-matrix-green/10"
          >
            <CircleUserRound size={21} />

            <span className="hidden max-w-32 truncate text-xs sm:block">
              {user.display_name ||
                user.username ||
                user.name ||
                user.email ||
                "Member"}
            </span>

            <ChevronDown size={15} />
          </button>

          {open && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-72
                         overflow-hidden rounded-xl
                         border border-matrix-green/30
                         bg-[#080d0b] p-2
                         text-sm text-white shadow-2xl"
            >
              <div className="border-b border-matrix-green/20 px-3 py-3">
                <div className="flex items-center gap-3">
                  <CircleUserRound
                    size={34}
                    className="shrink-0 text-matrix-green"
                  />

                  <div className="min-w-0">
                    <p className="truncate font-semibold text-matrix-green">
                      {user.display_name ||
                        user.username ||
                        user.name ||
                        "Doriantelive Member"}
                    </p>

                    <p className="truncate text-xs text-white/50">
                      {user.email || "Signed in"}
                    </p>
                  </div>
                </div>
              </div>

              <Link
                role="menuitem"
                to="/stream-presets"
                onClick={() => setOpen(false)}
                className="mt-2 flex items-center gap-3 rounded-lg
                           px-3 py-3 hover:bg-matrix-green/10"
              >
                <SlidersHorizontal size={18} />
                Stream Preset Manager
              </Link>

              <div
                className="flex items-center gap-3 px-3 py-3
                           text-white/35"
                aria-disabled="true"
              >
                <LayoutDashboard size={18} />
                Member Dashboard — Soon
              </div>

              <div
                className="flex items-center gap-3 px-3 py-3
                           text-white/35"
                aria-disabled="true"
              >
                <Settings size={18} />
                Settings — Soon
              </div>

              <div
                className="flex items-center gap-3 px-3 py-3
                           text-white/35"
                aria-disabled="true"
              >
                <UserRound size={18} />
                Account — Soon
              </div>

              <div className="mt-2 border-t border-white/10 pt-2">
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="flex w-full items-center gap-3
                             rounded-lg px-3 py-3 text-left
                             text-red-400 hover:bg-red-500/10
                             disabled:opacity-50"
                >
                  <LogOut size={18} />
                  {loggingOut ? "Logging out..." : "Log Out"}
                </button>

                {error && (
                  <p className="px-3 py-2 text-xs text-red-400">
                    {error}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}